import { EyeOutlined } from '@ant-design/icons';
import type { TableColumnsType, TableProps } from 'antd';
import { Button, Space, Table, Tag } from 'antd';
import { Form, Formik } from 'formik';
import { observer } from 'mobx-react-lite';
import { ParsedQuery } from 'query-string';
import React, { useEffect, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '../../../../../config/config';
import logStore from '../../../../../stores/logStore';
import { formatDateString } from '../../../../../utils/dateFunctions';
import { exportToExcel } from '../../../../../utils/excelFunction';
import { convertToDayjs, formattedDates, getFirstFilterDates } from '../../../../../utils/functions';
import { getActionColor, getActionLog } from '../../../../../utils/statusLogMapper';
import ActionButton from '../../../../atoms/ButtonComponent';
import DatePickerRangeComponet from '../../../../atoms/DatePickerRangeComponet';
import InputComponent from '../../../../atoms/InputComponet';
import ContentBox from '../../../../molecules/ContentBox';
import ModalLogComparativo from '../ModalLogComparativo';

interface DataType {
    file: boolean | React.Key;
    name: boolean | React.Key;
    type: boolean | React.Key;
    idUser: any;
    email: any;
    username: any;
    description: string;
    code: string;
    uuid: string;
    key: React.Key;
}

interface QueryParams {
    modifiedBy: undefined;
    endDate: any;
    startDate: any;
    size?: number;
    page?: number;
    sort?: string;
}

interface LogOrderProps {
    setQueryParams: (params: Record<string, any>) => void;
    queryParams: ParsedQuery<string>;
}

const LogOrder: React.FC<LogOrderProps> = observer(({ setQueryParams, queryParams }) => {
    const { logOrder, loading, pageOrder, totalElementsOrder, defaultparams, sort, sizeOrder } = logStore;
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [itemSelected, setItemSelected] = useState<any>();
    const [initialDate, finalDate] = getFirstFilterDates();

    const params = {
        entityName: 'TresCoracoesOrder',
        startDate: queryParams.startDate
            ? queryParams.startDate
            : initialDate + "T00:00:00.556",
        endDate: queryParams.endDate
            ? queryParams.endDate
            : finalDate + "T23:59:59.999",
        page: queryParams.page || 0,
        size: queryParams.size || DEFAULT_PAGE_SIZE,
        modifiedBy: queryParams.modifiedBy || undefined,
        sort: 'created',
    }

    useEffect(() => {
        const fetchData = async () => {
            await logStore.getListOrder(params);
        };

        fetchData();
    }, []);


    // Colunas da Tabela
    const columns: TableColumnsType<DataType> = [
        {
            title: 'Código',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Número',
            dataIndex: 'idOrder',
            key: 'idOrder',
        },
        {
            title: 'Cliente',
            dataIndex: 'modifiedBy',
            key: 'modifiedBy',
        },
        {
            title: 'Nome de Contato',
            dataIndex: 'contactName',
            key: 'contactName',
        },
        {
            title: 'Modificação',
            dataIndex: 'modifiedDate',
            key: 'modifiedDate',
            render: (item) => <p>{formatDateString(item)}</p>,
        },
        {
            title: 'Ação',
            dataIndex: 'operation',
            key: 'operation',
            render: (item) => {
                return (
                    <>{item && <Tag color={getActionColor(item)}> <p>{getActionLog(item)}</p></Tag>}  </>
                );
            },
        },
        {
            title: 'Visualizar',
            dataIndex: 'view',
            key: 'view',
            align: 'center',
            width: 30,
            render: (text, record) => (
                <Space className="cursor-pointer">
                    <div onClick={() => showModal(record)}>
                        <div><EyeOutlined /></div>
                    </div>
                </Space>
            ),
        },
    ];


    const showModal = (record: any) => {
        setItemSelected(record);
        setIsModalOpen(true);
    };


    const handleExport = async () => {
        const dataExport = await logStore.getListOrder(params);

        if (dataExport) {
            const listExport = dataExport?.data?.content.map((item: any) => ({
                actionType: getActionLog(item?.operation),
                userDetails: item?.id,
                user: item?.modifiedBy,
                modified: formatDateString(item.modifiedDate ?? ""),
            }));

            exportToExcel(listExport, ["Ação", "ID Usuário", "Usuário", "Modificação"], "Log de Usuario.xlsx");
        }
    };

    // Faz o filtro da tabela
    const handleSubmit = async (values: { modifiedBy: string, date: any }) => {
        const formattedDate = formattedDates(values?.date, 'YYYY-MM-DD');
        const dateInitial = formattedDate[0];
        const dateFinal = formattedDate[1];

        const newParams: any = {
            modifiedBy: values?.modifiedBy || undefined,
        };

        // Cria os parâmetros base
        const params: Record<string, any> = {
            size: queryParams.size ? Number(queryParams.size) : DEFAULT_PAGE_SIZE,
            page: 0,
            entityName: 'TresCoracoesOrder',
            modifiedBy: values?.modifiedBy || undefined,
            startDate: dateInitial ? `${dateInitial}T00:00:00` : undefined,
            endDate: dateFinal ? `${dateFinal}T23:59:59` : undefined,
        };

        Object.keys(params).forEach((key) => {
            if (params[key] === undefined) {
                delete params[key];
            }
        });

        setQueryParams(newParams);

        // //@ts-ignore
        await logStore.getListOrder(params);
    };

    // Limpa os dados do formulario
    const handleClearAndSearch = async (resetForm: () => void) => {
        resetForm();
        setQueryParams({});

        const clearParams = {
            entityName: 'TresCoracoesOrder',
            startDate: initialDate + "T00:00:00.556",
            endDate: finalDate + "T23:59:59.999",
            page: 0,
            size: DEFAULT_PAGE_SIZE,
            sort: 'created',
        }

        await logStore.getListOrder(clearParams);
    };

    const initialValues = {
        modifiedBy: Array.isArray(queryParams.modifiedBy) ? queryParams.modifiedBy[0] || '' : queryParams.modifiedBy || '',
        date:
            queryParams.startDate && queryParams.endDate
                ? [convertToDayjs(queryParams.startDate), convertToDayjs(queryParams.endDate)]
                : [convertToDayjs(initialDate), convertToDayjs(finalDate)],
    };

    const onChange: TableProps<DataType>['onChange'] = async (pagination, filters, sorter, extra) => {
        //@ts-ignore
        const params = buildParams(pagination, sorter, queryParams);
        await logStore.getListOrder(params);
    };

    const buildParams = (
        pagination: any,
        sorter: any,
        queryParams: QueryParams
    ) => {
        const currentPage = pagination.current - 1;
        const newPageSize = pagination.pageSize || DEFAULT_PAGE_SIZE;

        const params = {
            entityName: 'TresCoracoesOrder',
            startDate: queryParams.startDate
                ? queryParams.startDate
                : initialDate + "T00:00:00.556",
            endDate: queryParams.endDate
                ? queryParams.endDate
                : finalDate + "T23:59:59.999",
            size: pagination.pageSize || queryParams.size || newPageSize,
            page: pagination.current ? currentPage : queryParams.page || 0,
            modifiedBy: queryParams.modifiedBy || undefined,
            sort: 'created',
        }

        // Atualiza a URL com os novos parâmetros
        setQueryParams({
            size: params.size,
            page: params.page,
        });

        return params;
    };


    return (
        <>
            <ContentBox borderCustom='rounded-b-lg' className="mb-4 p-6" title="Log de Pedidos" description='Tela de logs de pedidos'>
                <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
                    {({ resetForm }) => (
                        <Form>
                            <div className="flex items-end w-full gap-4 pb-4">
                                <div className="w-full">
                                    <InputComponent label="Usuário" name="modifiedBy" id="modifiedBy" />
                                </div>
                                <div className="w-full">
                                    <DatePickerRangeComponet label="Data" name="date" />
                                </div>
                                <div className="flex items-end gap-4 justify-end ">
                                    <Button onClick={() => handleClearAndSearch(resetForm)}>
                                        Limpar
                                    </Button>
                                    <Button htmlType="submit" type="primary">Filtrar</Button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </ContentBox>


            <ContentBox className="mb-4 p-6" title="Log de Pedidos" description='Tela de logs de Pedidos' actions={
                <>
                    <ActionButton icon actionType="export" onClick={() => handleExport()}>Exportar para Excel</ActionButton>
                </>
            }>

                <Table
                    className="table-custom"
                    columns={columns}
                    dataSource={logOrder.map((item: any, index: number) => ({
                        ...item,
                        key: item.uuid || index,
                    }))}
                    loading={loading}
                    onChange={onChange}
                    size="middle"
                    bordered={false}
                    pagination={{
                        current: pageOrder + 1,
                        pageSize: sizeOrder,
                        total: totalElementsOrder,
                        showSizeChanger: true,
                        pageSizeOptions: ['20', '40', '60'],
                    }}
                    showSorterTooltip={{ target: 'sorter-icon' }}
                />
            </ContentBox>

            {isModalOpen && <ModalLogComparativo setIsModalOpen={setIsModalOpen} isModalOpen={isModalOpen} itemSelected={itemSelected} />}
        </>
    );
});

export default LogOrder;